import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Comic,
  ComicFormat,
  ComicGroup,
  ComicInput,
  ComicSeries,
  ExternalId,
  FORMATS,
  FORMAT_NAMES,
  READ_STATUS_LABELS,
  ReadStatus,
  fromList,
  seriesLabel,
  toList,
} from '../types';
import {
  ComicSearchResult,
  SOURCE_NAMES,
  fetchDetail,
  formatForSeriesType,
  hasDetail,
  isCollectedType,
  isSingleBook,
  splitSeriesTitle,
} from '../comicSearch';
import CoverImage from './CoverImage';
import ComicSearchField from './ComicSearchField';

interface ComicFormModalProps {
  open: boolean;
  // null = adding
  comic: Comic | null;
  // Adding from a series dialog starts in that series
  presetSeriesId?: string | null;
  series: ComicSeries[];
  groups: ComicGroup[];
  onClose: () => void;
  // Resolves true when saved, so the form can close
  onSave: (input: ComicInput) => Promise<boolean>;
  // Picking a whole series from search adds it; resolves to its id
  onCreateSeries: (
    result: ComicSearchResult,
    extras: { coverUrl: string; publisher: string }
  ) => Promise<string | null>;
  // Opens a series' checklist; picking a whole run goes straight there
  onOpenSeries: (id: string) => void;
}

const READ_STATUSES: ReadStatus[] = ['unread', 'started', 'read', 'wantToRead'];

const parseNumber = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const defaultFormat = (series: ComicSeries | null): ComicFormat =>
  series ? (series.trackBy === 'issue' ? 'issue' : 'trade') : 'graphicNovel';

const sameText = (a?: string, b?: string) =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

// Trades and books are numbered in their titles: "Saga Vol. 3"
const bookFormat = (title: string): ComicFormat =>
  /\b(vol\.?|volume|book)\s*\d/i.test(title) ? 'trade' : 'graphicNovel';

// Add and edit. Adding starts with a search that fills the form; every field
// can still be typed by hand.
const ComicFormModal = ({
  open,
  comic,
  presetSeriesId = null,
  series,
  groups,
  onClose,
  onSave,
  onCreateSeries,
  onOpenSeries,
}: ComicFormModalProps) => {
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState<ComicFormat>('graphicNovel');
  const [seriesId, setSeriesId] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [owned, setOwned] = useState(true);
  const [readStatus, setReadStatus] = useState<ReadStatus>('unread');
  const [coverUrl, setCoverUrl] = useState('');
  const [writers, setWriters] = useState('');
  const [artists, setArtists] = useState('');
  const [publisher, setPublisher] = useState('');
  const [notes, setNotes] = useState('');
  const [externalId, setExternalId] = useState<ExternalId | null>(null);
  // A line under the search saying what was filled in
  const [filledNote, setFilledNote] = useState<string | null>(null);
  // Search fills the details, so after a pick they fold away
  const [showMore, setShowMore] = useState(true);
  const [saving, setSaving] = useState(false);

  const sortedSeries = useMemo(
    () => [...series].sort((a, b) => seriesLabel(a).localeCompare(seriesLabel(b))),
    [series]
  );
  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.sortOrder - b.sortOrder),
    [groups]
  );
  const selectedSeries = series.find((entry) => entry.id === seriesId) ?? null;

  /* Fill the form each time it opens */
  useEffect(() => {
    if (!open) return;
    const preset = series.find((entry) => entry.id === presetSeriesId) ?? null;
    setTitle(comic?.title ?? '');
    setFormat(comic?.format ?? defaultFormat(preset));
    setSeriesId(comic ? comic.seriesId : preset?.id ?? null);
    setGroupId(comic?.groupId ?? null);
    setNumber(comic?.number != null ? String(comic.number) : '');
    setOwned(comic?.owned ?? true);
    setReadStatus(comic?.readStatus ?? 'unread');
    setCoverUrl(comic?.coverUrl ?? '');
    setWriters(fromList(comic?.writers));
    setArtists(fromList(comic?.artists));
    setPublisher(comic?.publisher ?? preset?.publisher ?? '');
    setNotes(comic?.notes ?? '');
    setExternalId(comic?.externalId ?? null);
    setFilledNote(null);
    setShowMore(true);
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSeriesChange = (next: ComicSeries | null) => {
    setSeriesId(next?.id ?? null);
    if (next) {
      setGroupId(null);
      if (!publisher && next.publisher) setPublisher(next.publisher);
    }
    // Only guess the format for new comics
    if (!comic) setFormat(defaultFormat(next));
  };

  // Match a search result to a series already in the library: by its id
  // at the same source first, then by name (and year when both have one).
  const findSeries = (
    source: ComicSearchResult['source'],
    externalSeriesId?: string | null,
    name?: string,
    year?: number | null
  ) =>
    series.find(
      (entry) =>
        externalSeriesId &&
        entry.externalId?.source === source &&
        entry.externalId.id === externalSeriesId
    ) ??
    series.find(
      (entry) =>
        sameText(entry.title, name) && (!year || !entry.startYear || entry.startYear === year)
    ) ??
    null;

  // Details arrive after a pick; a newer pick makes older ones stale
  const pickToken = useRef(0);

  const handlePick = async (result: ComicSearchResult) => {
    const token = ++pickToken.current;
    const isCurrent = () => token === pickToken.current;
    const source = SOURCE_NAMES[result.source];
    const detailPromise = hasDetail(result)
      ? fetchDetail(result).catch((err) => {
          console.error(err);
          return null;
        })
      : Promise.resolve(null);

    // A whole run: find (or add) the series and open its checklist instead
    if (result.kind === 'volume' && !isSingleBook(result)) {
      let id = findSeries(result.source, result.id, result.title, result.year)?.id ?? null;
      if (!id) {
        const detail = await detailPromise;
        id = await onCreateSeries(result, {
          coverUrl: detail?.coverUrl || result.coverUrl,
          publisher: detail?.publisher || result.publisher || '',
        });
      }
      if (!id || !isCurrent()) return;
      onClose();
      onOpenSeries(id);
      return;
    }

    setCoverUrl(result.coverUrl);
    if (result.publisher) setPublisher(result.publisher);
    setShowMore(false);

    setExternalId({ source: result.source, id: result.id });

    if (result.kind === 'issue') {
      const { name: seriesName, year: seriesYear } = splitSeriesTitle(result.seriesTitle);
      const numberText = result.number != null ? String(result.number) : '';
      setTitle(result.title);
      setNumber(numberText);
      setFormat(formatForSeriesType(result.seriesType) ?? 'issue');

      // Its series comes along too, so later issues land in the same place
      let parent = findSeries(result.source, result.seriesExternalId, seriesName, seriesYear);
      let parentId = parent?.id ?? null;
      if (!parentId && result.seriesExternalId && seriesName) {
        parentId = await onCreateSeries(
          {
            source: result.source,
            id: result.seriesExternalId,
            kind: 'volume',
            title: seriesName,
            year: seriesYear,
            coverUrl: '',
            thumbUrl: '',
            seriesType: result.seriesType || 'Single Issue',
          },
          { coverUrl: result.coverUrl, publisher: result.publisher || '' }
        );
        if (!isCurrent()) return;
      }
      if (!parent && parentId) parent = series.find((entry) => entry.id === parentId) ?? null;
      setSeriesId(parentId);
      if (parentId) setGroupId(null);
      if (!formatForSeriesType(result.seriesType) && parent) setFormat(defaultFormat(parent));
      setFilledNote(
        parent
          ? `Filled from ${source}, in ${seriesLabel(parent)}.`
          : parentId
          ? `Filled from ${source}, and added its series ${seriesName}.`
          : `Filled from ${source}. Its series isn't known, so it's added as a standalone.`
      );

      const detail = await detailPromise;
      if (!detail || !isCurrent()) return;
      const format = formatForSeriesType(detail.seriesType);
      if (format) setFormat(format);
      // Trades read better as "Saga Vol. 3: …" than "Saga #3"
      if (isCollectedType(detail.seriesType) && numberText) {
        // Skip a collection title that only repeats the number ("Volume Three")
        const subtitle =
          detail.storyTitle && !/^(vol\.?|volume|book)\b/i.test(detail.storyTitle)
            ? `: ${detail.storyTitle}`
            : '';
        setTitle(`${seriesName} Vol. ${numberText}${subtitle}`);
      }
      if (!result.coverUrl && detail.coverUrl) setCoverUrl(detail.coverUrl);
      if (detail.publisher) setPublisher((current) => current || detail.publisher || '');
      setWriters((current) => current || detail.writers.join(', '));
      setArtists((current) => current || detail.artists.join(', '));
      return;
    }

    // A one-shot, graphic novel or book: a standalone
    setTitle(result.title);
    setSeriesId(null);
    setNumber('');
    setFormat(formatForSeriesType(result.seriesType) ?? bookFormat(result.title));
    setWriters(result.writers?.join(', ') ?? '');
    setArtists('');
    setFilledNote(`Filled from ${source}. Check the title and format before adding.`);

    const detail = await detailPromise;
    if (!detail || !isCurrent()) return;
    if (detail.coverUrl) setCoverUrl((current) => current || detail.coverUrl || '');
    if (detail.publisher) setPublisher((current) => current || detail.publisher || '');
    setWriters((current) => current || detail.writers.join(', '));
    setArtists((current) => current || detail.artists.join(', '));
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await onSave({
      title: title.trim(),
      format,
      seriesId,
      groupId: seriesId ? null : groupId,
      number: seriesId ? parseNumber(number) : null,
      owned,
      readStatus,
      coverUrl: coverUrl.trim(),
      writers: toList(writers),
      artists: toList(artists),
      publisher: publisher.trim(),
      notes: notes.trim(),
      externalId,
    });
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{comic ? 'Edit Comic' : 'Add Comic'}</DialogTitle>

      <DialogContent dividers>
        {!comic && (
          <Box sx={{ mb: 2.5 }}>
            <ComicSearchField
              placeholder="Search for a comic, series or graphic novel"
              onPick={handlePick}
            />
            {filledNote && (
              <Alert
                severity="success"
                sx={{ mt: 1.5 }}
                onClose={() => setFilledNote(null)}
              >
                {filledNote}
              </Alert>
            )}
          </Box>
        )}

        {/* Cover beside the title. The status buttons sit under the title
            on wider screens, and get a full-width row of their own on phones */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '84px minmax(0, 1fr)', sm: '110px minmax(0, 1fr)' },
            gridTemplateAreas: {
              xs: '"cover title" "status status"',
              sm: '"cover title" "cover status"',
            },
            alignItems: 'start',
            columnGap: 2,
            rowGap: 2,
          }}
        >
          <Box sx={{ gridArea: 'cover', borderRadius: 1, overflow: 'hidden' }}>
            <CoverImage src={coverUrl.trim() || undefined} title={title || 'Cover'} />
          </Box>

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus={Boolean(comic)}
            required
            fullWidth
            placeholder="e.g. Vol. 1: I Am Gotham"
            sx={{ gridArea: 'title' }}
          />

          <Box sx={{ gridArea: 'status', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={readStatus}
              onChange={(_, next: ReadStatus | null) => next && setReadStatus(next)}
              aria-label="Read status"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {READ_STATUSES.map((status) => (
                <ToggleButton
                  key={status}
                  value={status}
                  sx={{ textTransform: 'none', px: { xs: 0.5, sm: 1.5 }, flex: { xs: 1, sm: 'none' } }}
                >
                  {READ_STATUS_LABELS[status]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <FormControlLabel
              sx={{ mr: 0 }}
              control={<Switch checked={owned} onChange={(e) => setOwned(e.target.checked)} />}
              label="Owned"
            />
          </Box>
        </Box>

        <Button
          onClick={() => setShowMore((prev) => !prev)}
          aria-expanded={showMore}
          endIcon={
            <ExpandMoreIcon
              sx={{ transition: 'transform 0.2s ease', transform: showMore ? 'rotate(180deg)' : 'none' }}
            />
          }
          sx={{ mt: 1.5, ml: -1, px: 1, color: 'text.secondary' }}
        >
          More details
        </Button>

        <Collapse in={showMore}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
            <Autocomplete
              options={sortedSeries}
              value={selectedSeries}
              onChange={(_, next) => handleSeriesChange(next)}
              getOptionLabel={seriesLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Series"
                  placeholder="None: standalone"
                  helperText={selectedSeries ? undefined : 'Leave empty for a standalone graphic novel'}
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="comic-form-format-label">Format</InputLabel>
                <Select
                  labelId="comic-form-format-label"
                  value={format}
                  label="Format"
                  onChange={(e) => setFormat(e.target.value as ComicFormat)}
                >
                  {FORMATS.map((entry) => (
                    <MenuItem key={entry} value={entry}>
                      {FORMAT_NAMES[entry]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedSeries ? (
                <TextField
                  label={selectedSeries.trackBy === 'issue' ? 'Issue #' : 'Volume #'}
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  type="number"
                  sx={{ width: 140, flexShrink: 0 }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="comic-form-group-label">Group</InputLabel>
                  <Select
                    labelId="comic-form-group-label"
                    value={groupId ?? ''}
                    label="Group"
                    onChange={(e) => setGroupId(e.target.value || null)}
                  >
                    <MenuItem value="">
                      <em>No group</em>
                    </MenuItem>
                    {sortedGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                label="Writers"
                value={writers}
                onChange={(e) => setWriters(e.target.value)}
                helperText="Separate with commas"
                fullWidth
              />
              <TextField
                label="Artists"
                value={artists}
                onChange={(e) => setArtists(e.target.value)}
                helperText="Separate with commas"
                fullWidth
              />
            </Box>

            <TextField
              label="Publisher"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              fullWidth
            />

            <TextField
              label="Cover image URL"
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              fullWidth
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Box>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!title.trim() || saving}>
          {saving ? 'Saving…' : comic ? 'Save' : 'Add Comic'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComicFormModal;
